import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ContentLayout from '../../users/components/ContentLayout';
import TableSection from '../../users/components/TableSection';
import DeleteModal from '../../../components/modals/DeleteModal';
import ContactModal from '../components/modals/ContactModal';
import { deleteContact, fetchContacts } from '../api/contactsApi';
import { useAuth } from '../../../context/useAuth';
import { PAGE_SIZE } from '../../../config';
import { isStaff } from '../../../shared/utils/roles';

const getContactFullName = (contact) =>
  `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim();

export default function ContactsPage() {
  const { user } = useAuth();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [contacts, setContacts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [message, setMessage] = useState('');

  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteDone, setDeleteDone] = useState(false);
  const latestContactsRequestRef = useRef(0);

  const canManage = isStaff(user);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const loadContacts = useCallback(
    async (page = currentPage) => {
      const requestId = latestContactsRequestRef.current + 1;
      latestContactsRequestRef.current = requestId;
      setListLoading(true);

      const { data, ok, message } = await fetchContacts({
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch,
      });

      if (requestId !== latestContactsRequestRef.current) {
        return;
      }

      setListLoading(false);

      if (ok && data) {
        const resolvedPage = data?.pagination?.currentPage || 1;
        setContacts(data.contacts || []);
        if (resolvedPage !== page) {
          setCurrentPage(resolvedPage);
        }
        setTotalPages(data?.pagination?.totalPages || 1);
        setTotalResults(data?.pagination?.totalCount ?? null);
      } else if (message) {
        setMessage(message);
      }
    },
    [currentPage, debouncedSearch]
  );

  useEffect(() => {
    loadContacts(currentPage);
  }, [loadContacts]);

  const handleCreateOpen = () => {
    setSelectedContact(null);
    setIsContactModalOpen(true);
  };

  const handleEditOpen = (contact) => {
    setSelectedContact(contact);
    setIsContactModalOpen(true);
  };

  const handleContactModalClose = () => {
    setSelectedContact(null);
    setIsContactModalOpen(false);
  };

  const handleDeleteOpen = (contact) => {
    setContactToDelete(contact);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteClose = () => {
    setContactToDelete(null);
    setDeleteLoading(false);
    setDeleteDone(false);
    setIsDeleteModalOpen(false);
  };

  const handleDeleteContact = async () => {
    if (!contactToDelete?._id) return;

    setDeleteLoading(true);
    const response = await deleteContact(contactToDelete._id);

    if (!response.ok) {
      setMessage(response.message);
      setDeleteLoading(false);
      return;
    }

    setDeleteDone(true);
    setMessage(response.message || 'Kontakt obrisan.');
    await loadContacts();

    setTimeout(() => {
      handleDeleteClose();
    }, 700);
  };

  const columns = useMemo(
    () => [
      { key: 'firstName', label: 'Ime', render: (val) => val || '-' },
      { key: 'lastName', label: 'Prezime', render: (val) => val || '-' },
      { key: 'info', label: 'Informacije', render: (val) => val || '-' },
      { key: 'phone', label: 'Broj telefona', render: (val) => val || '-' },
      { key: 'email', label: 'Email', render: (val) => val || '-' },
    ],
    []
  );

  return (
    <>
      <ContentLayout>
        <TableSection
          showCreateButton={canManage}
          createButtonDisplay="Dodaj kontakt"
          onCreateClick={handleCreateOpen}
          onSearch={setSearch}
          columns={columns}
          actions={[
            {
              key: 'edit',
              label: 'Izmeni kontakt',
              icon: (
                <img
                  src="/assets/icons/edit-icon.svg"
                  alt="Izmeni"
                  width={16}
                  height={16}
                  className="dropdown-icon"
                />
              ),
              onClick: handleEditOpen,
              visible: () => canManage,
            },
            {
              key: 'delete',
              label: 'Obrisi kontakt',
              icon: (
                <img
                  src="/assets/icons/delete.svg"
                  alt="Obriši"
                  width={16}
                  height={16}
                  className="dropdown-icon"
                />
              ),
              onClick: handleDeleteOpen,
              visible: () => canManage,
              danger: true,
            },
          ]}
          data={contacts}
          emptyState={{
            icon: '/assets/icons/no-users.svg',
            title: 'Trenutno nema kontakata',
            description: 'Dodajte kontakt.',
          }}
          showPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalResults}
          paginationLoading={listLoading}
          onPageChange={setCurrentPage}
          searchPlaceholder="Pretraži kontakte"
        />
      </ContentLayout>
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={handleContactModalClose}
        onSaved={loadContacts}
        contact={selectedContact}
      />
      <DeleteModal
        visible={isDeleteModalOpen}
        onDelete={handleDeleteContact}
        onCancel={handleDeleteClose}
        loading={deleteLoading}
        done={deleteDone}
        title="Brisanje kontakta"
        message={`Da li ste sigurni da želite da obrišete ${getContactFullName(
          contactToDelete
        )} iz kontakata?`}
        buttonText="Obrisi kontakt"
      />
      {!!message && <span style={{ display: 'none' }}>{message}</span>}
    </>
  );
}
